import React from 'react';
import { Alert, Box, Text } from '@mantine/core';
import { IconWifiOff } from '@tabler/icons-react';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';

/**
 * OfflineBanner Component
 *
 * Displays a persistent banner at the top of the page when the user goes offline.
 * Automatically hides when the user reconnects.
 *
 * Features:
 * - Uses useNetworkStatus hook to monitor connectivity
 * - Only renders when offline (no banner when online)
 * - Fixed positioning at top of viewport
 * - Eye-catching orange/yellow warning style
 * - Icon + clear messaging
 *
 * @example
 * ```tsx
 * // In App.tsx
 * <OfflineBanner />
 * <YourMainContent />
 * ```
 */
export const OfflineBanner: React.FC = (): React.ReactElement | null => {
  const { isOnline } = useNetworkStatus();

  // Don't render anything if online
  if (isOnline) {
    return null;
  }

  return (
    <Box
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        width: '100%',
      }}
    >
      <Alert
        variant="filled"
        color="orange"
        icon={<IconWifiOff size={20} />}
        title="No Internet Connection"
        styles={{
          root: {
            borderRadius: 0,
          },
        }}
      >
        <Text size="sm">
          You are currently offline. Some features may not be available until your connection is restored.
        </Text>
      </Alert>
    </Box>
  );
};
